import React, { useState } from 'react';
import { Briefcase, Car, Upload, Calculator, CheckCircle } from 'lucide-react';
export const FastTrackPortal = () => {
  const [loanType, setLoanType] = useState<'salary' | 'logbook'>('salary');
  const [vehicleValue, setVehicleValue] = useState(2000000);
  const [monthlySalary, setMonthlySalary] = useState(80000);
  const maxLoanSalary = monthlySalary * 12 * 0.7; // 70% of annual salary
  const maxLoanLogbook = vehicleValue * 0.7; // 70% of vehicle value
  return <section id="fast-track" className="py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-575">
      <div className="container" data-id="element-576">
        <div className="text-center mb-16" data-id="element-577">
          <h2 className="section-title" data-id="element-578">Fast-Track Cash Portal</h2>
          <p className="section-subtitle" data-id="element-579">
            Quick, secured financing for salaried employees and vehicle owners.
            Get approved in hours, not days.
          </p>
        </div>

        <div className="max-w-6xl mx-auto" data-id="element-580">
          <div className="grid lg:grid-cols-2 gap-8" data-id="element-581">
            {/* Salaried Check-Off Loans */}
            <div onClick={() => setLoanType('salary')} className={`bg-white rounded-2xl p-8 border-2 transition-all duration-300 cursor-pointer ${loanType === 'salary' ? 'border-primary-500 shadow-2xl' : 'border-gray-200 hover:border-primary-300'}`} data-id="element-582">
              <div className="flex items-center gap-4 mb-6" data-id="element-583">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center" data-id="element-584">
                  <Briefcase size={32} className="text-primary-600" data-id="element-585" />
                </div>
                <div data-id="element-586">
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-587">
                    Check-Off Salary Loans
                  </h3>
                  <p className="text-primary-600 font-semibold" data-id="element-588">
                    For Salaried Employees
                  </p>
                </div>
              </div>

              <ul className="space-y-3 mb-6" data-id="element-589">
                <li className="flex items-start gap-2" data-id="element-590">
                  <CheckCircle size={18} className="text-primary-600 flex-shrink-0 mt-0.5" data-id="element-591" />
                  <span className="text-gray-700" data-id="element-592">
                    Direct deduction from payroll for stability
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-593">
                  <CheckCircle size={18} className="text-primary-600 flex-shrink-0 mt-0.5" data-id="element-594" />
                  <span className="text-gray-700" data-id="element-595">
                    Lower interest rates (from 10% p.a.)
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-596">
                  <CheckCircle size={18} className="text-primary-600 flex-shrink-0 mt-0.5" data-id="element-597" />
                  <span className="text-gray-700" data-id="element-598">
                    Up to 70% of annual salary
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-599">
                  <CheckCircle size={18} className="text-primary-600 flex-shrink-0 mt-0.5" data-id="element-600" />
                  <span className="text-gray-700" data-id="element-601">
                    Flexible repayment up to 48 months
                  </span>
                </li>
              </ul>

              {loanType === 'salary' && <div className="bg-primary-50 rounded-xl p-6 animate-fadeIn" data-id="element-602">
                  <div className="flex items-center gap-3 mb-4" data-id="element-603">
                    <Calculator size={24} className="text-primary-600" data-id="element-604" />
                    <h4 className="font-bold text-gray-900" data-id="element-605">Loan Calculator</h4>
                  </div>
                  <div className="mb-4" data-id="element-606">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-607">
                      Monthly Salary (KSh)
                    </label>
                    <input type="range" min="30000" max="500000" step="10000" value={monthlySalary} onChange={e => setMonthlySalary(Number(e.target.value))} className="w-full" data-id="element-608" />
                    <div className="flex justify-between text-sm text-gray-600 mt-1" data-id="element-609">
                      <span data-id="element-610">KSh 30K</span>
                      <span className="font-bold text-primary-600" data-id="element-611">
                        KSh {(monthlySalary / 1000).toFixed(0)}K
                      </span>
                      <span data-id="element-612">KSh 500K</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-primary-200" data-id="element-613">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-614">
                      Your Maximum Loan Limit
                    </p>
                    <p className="text-3xl font-bold text-primary-600" data-id="element-615">
                      KSh {maxLoanSalary.toLocaleString()}
                    </p>
                  </div>
                </div>}

              <a href="#apply-now" className="block text-center mt-6 py-4 px-6 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg" data-id="element-616">
                Apply for Check-Off Loan
              </a>
            </div>

            {/* Logbook Loans */}
            <div onClick={() => setLoanType('logbook')} className={`bg-white rounded-2xl p-8 border-2 transition-all duration-300 cursor-pointer ${loanType === 'logbook' ? 'border-primary-500 shadow-2xl' : 'border-gray-200 hover:border-primary-300'}`} data-id="element-617">
              <div className="flex items-center gap-4 mb-6" data-id="element-618">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center" data-id="element-619">
                  <Car size={32} className="text-primary-600" data-id="element-620" />
                </div>
                <div data-id="element-621">
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-622">
                    Logbook Loans
                  </h3>
                  <p className="text-primary-600 font-semibold" data-id="element-623">
                    Secured by Your Vehicle
                  </p>
                </div>
              </div>

              <ul className="space-y-3 mb-6" data-id="element-624">
                <li className="flex items-start gap-2" data-id="element-625">
                  <CheckCircle size={18} className="text-primary-600 flex-shrink-0 mt-0.5" data-id="element-626" />
                  <span className="text-gray-700" data-id="element-627">
                    Up to 70% of vehicle value
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-628">
                  <CheckCircle size={18} className="text-primary-600 flex-shrink-0 mt-0.5" data-id="element-629" />
                  <span className="text-gray-700" data-id="element-630">Keep using your vehicle</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-631">
                  <CheckCircle size={18} className="text-primary-600 flex-shrink-0 mt-0.5" data-id="element-632" />
                  <span className="text-gray-700" data-id="element-633">Approval in 6 hours</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-634">
                  <CheckCircle size={18} className="text-primary-600 flex-shrink-0 mt-0.5" data-id="element-635" />
                  <span className="text-gray-700" data-id="element-636">
                    Flexible repayment terms
                  </span>
                </li>
              </ul>

              {loanType === 'logbook' && <div className="bg-primary-50 rounded-xl p-6 animate-fadeIn" data-id="element-637">
                  <div className="flex items-center gap-3 mb-4" data-id="element-638">
                    <Upload size={24} className="text-primary-600" data-id="element-639" />
                    <h4 className="font-bold text-gray-900" data-id="element-640">
                      Logbook Upload Widget
                    </h4>
                  </div>
                  <div className="mb-4" data-id="element-641">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-642">
                      Vehicle Value (KSh)
                    </label>
                    <input type="range" min="500000" max="10000000" step="100000" value={vehicleValue} onChange={e => setVehicleValue(Number(e.target.value))} className="w-full" data-id="element-643" />
                    <div className="flex justify-between text-sm text-gray-600 mt-1" data-id="element-644">
                      <span data-id="element-645">KSh 500K</span>
                      <span className="font-bold text-primary-600" data-id="element-646">
                        KSh {(vehicleValue / 1000000).toFixed(1)}M
                      </span>
                      <span data-id="element-647">KSh 10M</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-primary-200 mb-4" data-id="element-648">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-649">
                      Your Maximum Loan Limit
                    </p>
                    <p className="text-3xl font-bold text-primary-600" data-id="element-650">
                      KSh {maxLoanLogbook.toLocaleString()}
                    </p>
                  </div>
                  <div className="border-2 border-dashed border-primary-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors duration-200 cursor-pointer" data-id="element-651">
                    <Upload size={32} className="text-primary-600 mx-auto mb-2" data-id="element-652" />
                    <p className="text-sm font-semibold text-gray-700 mb-1" data-id="element-653">
                      Attach Your Logbook
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-654">
                      PDF, JPG, or PNG (Max 5MB)
                    </p>
                  </div>
                </div>}

              <a href="#apply-now" className="block text-center mt-6 py-4 px-6 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg" data-id="element-655">
                Apply for Logbook Loan
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};